import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs';
import { DateTime } from 'luxon';
import { APIResponse } from 'fakturnia-shared';
import { SubscriptionsApiService, FulfillmentPreview, FulfillmentResult } from 'shared/services/api/subscriptions.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';

export interface FulfillmentDialogData {
  orderId: string;
  paidAt?: string; // ISO date string
}

type DialogPhase = 'loading' | 'preview' | 'processing' | 'result' | 'error';

const STAGE_NAMES: Record<number, string> = {
  0: 'FREE',
  1: 'START',
  2: 'PRO',
  3: 'BUSINESS'
};

@Component({
  selector: 'app-fulfillment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCheckboxModule,
    FormsModule
  ],
  templateUrl: './fulfillment-dialog.component.html',
  styleUrls: ['./fulfillment-dialog.component.scss']
})
export class FulfillmentDialogComponent implements OnInit {
  phase: DialogPhase = 'loading';
  preview: FulfillmentPreview | null = null;
  result: FulfillmentResult | null = null;
  errorMessage: string = '';

  // Opcje fulfillmentu
  skipInvoice = false;
  skipEmail = false;
  skipNotification = false;
  skipDiscord = false;

  constructor(
    public dialogRef: MatDialogRef<FulfillmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FulfillmentDialogData,
    private _subscriptionsApi: SubscriptionsApiService,
    private _snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    this._loadPreview();
  }

  private _loadPreview() {
    this.phase = 'loading';

    this._subscriptionsApi.getFulfillmentPreview(this.data.orderId).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        if (response.success && response.data) {
          this.preview = response.data;
          this.phase = 'preview';
        } else {
          this.errorMessage = response.message || 'Nie udało się pobrać podglądu realizacji.';
          this.phase = 'error';
        }
      },
      error: () => {
        this.errorMessage = 'Wystąpił błąd połączenia.';
        this.phase = 'error';
      }
    });
  }

  confirmFulfillment() {
    this.phase = 'processing';

    this._subscriptionsApi.updateOrderStatus(
      this.data.orderId,
      'paid',
      this.data.paidAt,
      {
        skipInvoice: this.skipInvoice,
        skipEmail: this.skipEmail,
        skipNotification: this.skipNotification,
        skipDiscord: this.skipDiscord
      }
    ).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        if (response.success && response.data?.fulfillment) {
          this.result = response.data.fulfillment;
          this.phase = 'result';
        } else {
          this.errorMessage = response.message || 'Nie udało się zrealizować zamówienia.';
          this.phase = 'error';
        }
      },
      error: () => {
        this.errorMessage = 'Wystąpił błąd połączenia.';
        this.phase = 'error';
      }
    });
  }

  close(updated: boolean = false) {
    this.dialogRef.close({ action: updated ? 'updated' : null });
  }

  getStageName(stage: number): string {
    return STAGE_NAMES[stage] || 'UNKNOWN';
  }

  getStageColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'text-gray-600 dark:text-gray-400',
      1: 'text-blue-600 dark:text-blue-400',
      2: 'text-purple-600 dark:text-purple-400',
      3: 'text-amber-600 dark:text-amber-400'
    };
    return colors[stage] || 'text-gray-600';
  }

  getStageBgColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'bg-gray-100 dark:bg-gray-700',
      1: 'bg-blue-100 dark:bg-blue-900/30',
      2: 'bg-purple-100 dark:bg-purple-900/30',
      3: 'bg-amber-100 dark:bg-amber-900/30'
    };
    return colors[stage] || 'bg-gray-100';
  }

  formatPrice(priceInCents: number): string {
    return (priceInCents / 100).toFixed(2) + ' zł';
  }

  formatDocumentLimit(limit: number | null): string {
    return limit === null ? 'Bez limitu' : `${limit} dokumentów`;
  }

  formatDate(isoDate: string | null): string {
    if (!isoDate) return '-';
    return DateTime.fromISO(isoDate).setLocale('pl').toFormat('d MMMM yyyy');
  }
}
